import React from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { curtainRevealContainer, curtainRevealLine, goldenSpring } from '../../lib/fluid-animations';

interface KineticHeadlineProps {
  children: React.ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
  delay?: number;
}

/**
 * KineticHeadline - Word-by-word curtain reveal animation
 *
 * Children should be structured with line breaks (<br/>) to define
 * animation groups. Each line animates separately with stagger.
 */
const KineticHeadline: React.FC<KineticHeadlineProps> = ({
  children,
  className = '',
  as: Component = 'h1',
  delay = 0
}) => {
  const prefersReducedMotion = useReducedMotion();

  // Extract text content and structure from children
  const parseChildren = (children: React.ReactNode): React.ReactNode[] => {
    const lines: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (typeof child === 'string') {
        // Split string by line breaks or treat as single line
        const textLines = child.split('\n').filter(line => line.trim());
        textLines.forEach(line => {
          lines.push(line.trim());
        });
      } else if (React.isValidElement(child)) {
        if (child.type === 'br') {
          // br tags are line separators, handled implicitly
          return;
        }
        // Pass through other elements (like spans with styling)
        lines.push(child);
      }
    });

    return lines;
  };

  // Flatten children into animatable lines
  const flattenChildren = (children: React.ReactNode): React.ReactNode[][] => {
    const result: React.ReactNode[][] = [];
    let currentLine: React.ReactNode[] = [];

    const processNode = (node: React.ReactNode) => {
      if (node === null || node === undefined) return;

      if (typeof node === 'string') {
        // Add words to current line
        const words = node.split(/\s+/).filter(w => w);
        words.forEach((word, i) => {
          currentLine.push(word);
          if (i < words.length - 1) {
            currentLine.push(' ');
          }
        });
      } else if (React.isValidElement(node)) {
        if (node.type === 'br') {
          // Line break - push current line and start new one
          if (currentLine.length > 0) {
            result.push(currentLine);
            currentLine = [];
          }
        } else {
          // Element like <span> - process its children but keep wrapper
          const innerContent = node.props.children;
          if (typeof innerContent === 'string') {
            currentLine.push(
              React.cloneElement(node, { key: `elem-${result.length}-${currentLine.length}` })
            );
          } else {
            // For complex children, just add the whole element
            currentLine.push(node);
          }
        }
      }
    };

    React.Children.forEach(children, processNode);

    // Push remaining line
    if (currentLine.length > 0) {
      result.push(currentLine);
    }

    return result;
  };

  const lines = flattenChildren(children);

  // If reduced motion, render without animation
  if (prefersReducedMotion) {
    return (
      <Component className={className}>
        {children}
      </Component>
    );
  }

  return (
    <Component className={className}>
      <motion.span
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.12,
              delayChildren: delay
            }
          }
        }}
        className="block"
      >
        {lines.map((lineContent, lineIndex) => (
          <motion.span
            key={lineIndex}
            className="block overflow-hidden pb-[0.15em]" /* Extra padding for descenders (g, y, p) */
            style={{ perspective: '1000px' }}
          >
            <motion.span
              className="block"
              variants={curtainRevealLine}
              style={{ transformOrigin: 'bottom center' }}
            >
              {lineContent.map((item, itemIndex) => {
                if (typeof item === 'string') {
                  if (item === ' ') {
                    return <span key={`space-${lineIndex}-${itemIndex}`}>&nbsp;</span>;
                  }
                  return (
                    <span key={`word-${lineIndex}-${itemIndex}`}>
                      {item}
                      {itemIndex < lineContent.length - 1 && lineContent[itemIndex + 1] !== ' ' && ' '}
                    </span>
                  );
                }
                return item;
              })}
            </motion.span>
          </motion.span>
        ))}
      </motion.span>
    </Component>
  );
};

/**
 * Simple word-by-word animation variant
 * Each word reveals individually with stagger
 */
export const KineticText: React.FC<{
  text: string;
  className?: string;
  delay?: number;
}> = ({ text, className = '', delay = 0 }) => {
  const prefersReducedMotion = useReducedMotion();
  const words = text.split(' ');

  if (prefersReducedMotion) {
    return <span className={className}>{text}</span>;
  }

  return (
    <motion.span
      className={className}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.06,
            delayChildren: delay
          }
        }
      }}
    >
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden">
          <motion.span
            className="inline-block"
            variants={{
              hidden: { y: '100%', opacity: 0 },
              visible: {
                y: 0,
                opacity: 1,
                transition: goldenSpring
              }
            }}
          >
            {word}
            {i < words.length - 1 && '\u00A0'}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
};

export default KineticHeadline;
